import type { WeeklySlot } from '@gr/core/distribution/channels/dto';
import { normalizeSlots } from '@gr/core/distribution/channels/slots';

/**
 * Сетка слотов в редакторе (CHN-UC-3): строки — время, отмеченные клетки — «день@время».
 * Неизменяемая: каждое действие возвращает новую сетку.
 */
export interface SlotGrid {
  readonly times: readonly string[];
  readonly checked: ReadonlySet<string>;
}

const TIME = /^\d{2}:\d{2}$/;
const cell = (weekday: number, time: string): string => `${weekday}@${time}`;

export function slotGridOf(slots: readonly WeeklySlot[]): SlotGrid {
  return { times: [...new Set(slots.map((s) => s.time))].sort(), checked: new Set(slots.map((s) => cell(s.weekday, s.time))) };
}

export function isSlotOn(grid: SlotGrid, weekday: number, time: string): boolean {
  return grid.checked.has(cell(weekday, time));
}

export function toggleSlot(grid: SlotGrid, weekday: number, time: string): SlotGrid {
  const checked = new Set(grid.checked);
  const key = cell(weekday, time);
  if (checked.has(key)) checked.delete(key);
  else checked.add(key);
  return { times: grid.times, checked };
}

/** Время можно добавить, если оно в формате ЧЧ:ММ и такой строки ещё нет. */
export function canAddTime(grid: SlotGrid, time: string): boolean {
  return TIME.test(time) && !grid.times.includes(time);
}

export function addTime(grid: SlotGrid, time: string): SlotGrid {
  return canAddTime(grid, time) ? { times: [...grid.times, time].sort(), checked: grid.checked } : grid;
}

/** Убирает строку времени вместе с её отметками. */
export function removeTime(grid: SlotGrid, time: string): SlotGrid {
  return { times: grid.times.filter((t) => t !== time), checked: new Set([...grid.checked].filter((key) => !key.endsWith(`@${time}`))) };
}

export function slotsOf(grid: SlotGrid): WeeklySlot[] {
  return (
    normalizeSlots(
      [...grid.checked].map((key) => {
        const [weekday, time] = key.split('@');
        return { weekday: Number(weekday), time: time ?? '' };
      }),
    ) ?? []
  );
}
